import Button from '@/common/components/Button'
import { formatCurrency } from '@/common/utils/formatters'
import { UpdateContractCreditLimitsSchema } from '@/features/privateCompanies/schemas/contract/updateCreditLimits.schema'
import { ContractProductCategoryProductPriceData } from '@/features/publicCompanyLeadsFunnel/types/publicCompanyLead'
import { useIsFetching } from '@tanstack/react-query'
import { MapPinnedIcon, PlusIcon } from 'lucide-react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import ContractProductFixedPriceItem from './ContractProductFixedPriceItem'

interface ContractProductFixedPricesProps {
  productCategoryIndex: number
  productIndex: number
  contract_product_category_product_prices?: ContractProductCategoryProductPriceData[]
  disabled?: boolean
}

export const ContractProductFixedPrices = ({
  productCategoryIndex,
  productIndex,
  disabled,
}: ContractProductFixedPricesProps) => {
  const { publicCompanyLeadId } = useParams()

  const { control, setValue, clearErrors } = useFormContext<UpdateContractCreditLimitsSchema>()

  const {
    fields: productPrices,
    append: appendProductPrice,
    remove: removeProductPrice,
  } = useFieldArray({
    control,
    name: `contract.contract_product_categories_attributes.${productCategoryIndex}.contract_product_category_products.${productIndex}.contract_product_category_product_prices`,
  })

  const publicCompanyIsFetching = useIsFetching({ queryKey: ['publicCompanyLead', publicCompanyLeadId] })

  const resetValue = (index: number) => {
    setValue(
      `contract.contract_product_categories_attributes.${productCategoryIndex}.contract_product_category_products.${productIndex}.contract_product_category_product_prices.${index}.geo`,
      undefined,
    )

    setValue(
      `contract.contract_product_categories_attributes.${productCategoryIndex}.contract_product_category_products.${productIndex}.contract_product_category_product_prices.${index}.value`,
      formatCurrency(0),
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {productPrices?.map((productPrice, index) => (
        <ContractProductFixedPriceItem
          key={productPrice.id}
          productPrices={productPrices}
          productPrice={productPrice}
          productCategoryIndex={productCategoryIndex}
          productIndex={productIndex}
          index={index}
          onDelete={() => {
            removeProductPrice(index)
            if (productPrices.length === 1) {
              setValue(
                `contract.contract_product_categories_attributes.${productCategoryIndex}.contract_product_category_products.${productIndex}.filterByRegion`,
                false,
              )
              clearErrors(
                `contract.contract_product_categories_attributes.${productCategoryIndex}.contract_product_category_products.${productIndex}.price_rule`,
              )
            }
          }}
          disabled={!!publicCompanyIsFetching || disabled}
        />
      ))}

      <div className="flex items-center justify-between">
        <Button
          type="button"
          variant="outline-error"
          onClick={() => {
            setValue(
              `contract.contract_product_categories_attributes.${productCategoryIndex}.contract_product_category_products.${productIndex}.filterByRegion`,
              false,
            )
            clearErrors(
              `contract.contract_product_categories_attributes.${productCategoryIndex}.contract_product_category_products.${productIndex}.price_rule`,
            )

            productPrices?.map((_, index) => {
              resetValue(index)
            })
          }}
          disabled={!!publicCompanyIsFetching || disabled}
        >
          <MapPinnedIcon strokeWidth={1} className="size-4" />
          <p className="text-[11px]">Cancelar filtro por região</p>
        </Button>

        <Button
          type="button"
          variant="link"
          name={`contract_product_price_rule_add_region_${productIndex}`}
          onClick={() =>
            appendProductPrice({
              value: formatCurrency(0.001),
            })
          }
          disabled={!!publicCompanyIsFetching || disabled}
        >
          <PlusIcon strokeWidth={1} />
        </Button>
      </div>
    </div>
  )
}
